import dayjs, { Dayjs } from 'dayjs';
import i18n from 'i18next';
import { IGetDrinkRes } from '../types/api/care-settings/getDrink';
import { IGetLaxativeRes } from '../types/api/care-settings/getLaxative';
import { IDailyCareRecord, IGetDailyCareRecordRes } from '../types/api/traversal/getDailyCareRecord';
import {
  CARE_HISTORY_CSV_COL_EN,
  CARE_HISTORY_CSV_COL_JA,
  DIALY_CSV_COL_EN,
  DIALY_CSV_COL_JA,
  LAXATIVE_TYPE_JA,
  LAXATIVE_UNIT_JA,
  SIZE,
} from '../constants';
import {
  formatBooleanEn,
  formatBooleanJa,
  formatDefecationJa,
  formatDrinkingType,
  formatHardnessJa,
  formatIncontinenceJa,
  formatPadEn,
  formatPadJa,
  formatPostureEn,
  formatPostureJa,
  formatToiletEn,
  formatToiletJa,
  formatUrineJa,
} from './dataLabelUtil';
import { exportCSV } from './exportCSV';

interface ExportCareRecordCSVParams {
  data: IGetDailyCareRecordRes[];
  drinks?: IGetDrinkRes[];
  laxatives?: IGetLaxativeRes[];
  residentFullName?: string;
  start: Dayjs;
  end: Dayjs;
  currentNursingHomeName?: string | null;
  showMedicalContent: boolean;
}

const isJaLanguage = () => i18n.language.startsWith('ja');

const sanitizeFilenameSegment = (segment?: string | null) => {
  if (!segment) return '';
  const withoutControlChars = Array.from(segment)
    .map((char) => {
      const code = char.charCodeAt(0);
      return code >= 0 && code <= 31 ? '_' : char;
    })
    .join('');

  return withoutControlChars
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[. ]+$/g, '')
    .replace(/_+/g, '_');
};

const buildCsvFilename = ({
  currentNursingHomeName,
  residentFullName,
  csvTitle,
  start,
  end,
}: {
  currentNursingHomeName?: string | null;
  residentFullName?: string;
  csvTitle: string;
  start: Dayjs;
  end: Dayjs;
}) => {
  const segments = [
    sanitizeFilenameSegment(currentNursingHomeName),
    sanitizeFilenameSegment(residentFullName),
    sanitizeFilenameSegment(csvTitle),
    dayjs(start).format('YYYY-MM-DD'),
    dayjs(end).format('YYYY-MM-DD'),
  ].filter(Boolean);

  return segments.join('_') || 'care-record';
};

const insertPlace = (careRecord: IDailyCareRecord) => {
  if (careRecord.padType != null && careRecord.toiletType != null) {
    return isJaLanguage()
      ? `${formatPadJa(careRecord.padType)}/${formatToiletJa(careRecord.toiletType)}`
      : `${formatPadEn(careRecord.padType)}/${formatToiletEn(careRecord.toiletType)}`;
  }
  if (careRecord.padType != null) {
    return isJaLanguage() ? formatPadJa(careRecord.padType) : formatPadEn(careRecord.padType);
  }
  if (careRecord.toiletType != null) {
    return isJaLanguage() ? formatToiletJa(careRecord.toiletType) : formatToiletEn(careRecord.toiletType);
  }
  return '';
};

const insertDrinkingName = (careRecord: IDailyCareRecord, drinks?: IGetDrinkRes[]) => {
  if (careRecord.drinkingOther) {
    return careRecord.drinkingOther;
  }
  if (careRecord.drinkingType) {
    return formatDrinkingType(careRecord.drinkingType) ?? '';
  }
  if (careRecord.drinkingName) {
    return careRecord.drinkingName;
  }
  if (careRecord.drinkingId) {
    return drinks?.find((v) => v.id === careRecord.drinkingId)?.name ?? '';
  }
  return '';
};

const insertLaxativeName = (careRecord: IDailyCareRecord, laxatives?: IGetLaxativeRes[]) => {
  if (careRecord.laxativeName) {
    return careRecord.laxativeName;
  }
  if (!careRecord.laxativeId) return '';
  return laxatives?.find((v) => v.id === careRecord.laxativeId)?.name ?? '';
};

const insertBoolean = (data: boolean | null) => {
  return data === null ? '' : isJaLanguage() ? formatBooleanJa(data) : formatBooleanEn(data);
};

const insertPadUrineVolumeType = (careRecord: IDailyCareRecord) => {
  return isJaLanguage() ? formatUrineJa(careRecord.padUrineVolumeType ?? '') : (careRecord.padUrineVolumeType ?? '');
};

const insertIncontinence = (careRecord: IDailyCareRecord) => {
  return isJaLanguage() ? formatIncontinenceJa(careRecord.incontinence ?? '') : (careRecord.incontinence ?? '');
};

const insertDefecation = (careRecord: IDailyCareRecord) => {
  return isJaLanguage()
    ? (formatDefecationJa(careRecord.defecationVolumeType) ?? '')
    : (careRecord.defecationVolumeType ?? '');
};

const insertHardnessType = (careRecord: IDailyCareRecord) => {
  return isJaLanguage() ? formatHardnessJa(careRecord.hardnessType) : (careRecord.hardnessType ?? '');
};

const insertDiaper = (careRecord: IDailyCareRecord) => {
  return isJaLanguage() ? (careRecord.diaper ? SIZE[careRecord.diaper] : '') : (careRecord.diaper ?? '');
};

const insertLaxativeType = (careRecord: IDailyCareRecord) => {
  return isJaLanguage()
    ? careRecord.laxativeType
      ? LAXATIVE_TYPE_JA[careRecord.laxativeType]
      : ''
    : (careRecord.laxativeType ?? '');
};

const insertLaxativeAmount = (careRecord: IDailyCareRecord) => {
  if (!careRecord.laxativeAmount) return '';
  return isJaLanguage()
    ? `${careRecord.laxativeAmount}${careRecord.laxativeUnit ? LAXATIVE_UNIT_JA[careRecord.laxativeUnit] : ''}`
    : `${careRecord.laxativeAmount}${careRecord.laxativeUnit ?? ''}`;
};

const insertMlValue = (value: number | null | undefined) => {
  return value == null ? '' : `${value}ml`;
};

const insertPosture = (careRecord: IDailyCareRecord) => {
  return isJaLanguage() ? formatPostureJa(careRecord.posture) : formatPostureEn(careRecord.posture);
};

export const exportCareRecordCSV = async ({
  data,
  residentFullName,
  start,
  end,
  currentNursingHomeName,
  showMedicalContent,
  drinks,
  laxatives,
}: ExportCareRecordCSVParams) => {
  let exportData: Record<string, unknown>[] = [];

  if (!data.filter((v) => v.careRecords.length).length) {
    return false;
  }

  const csvTitle = showMedicalContent ? i18n.t('urinationDiary', '排尿日誌') : i18n.t('careHistory', 'ケア履歴');

  data.forEach((resident) => {
    resident.careRecords.forEach((v) => {
      const csvData = {
        ...v,
        residentId: resident.id,
        name: resident.name ?? '',
        facilityName: currentNursingHomeName ?? '',
        unitName: resident.nursingHomeUnitName ?? '',
        roomName: resident.roomName ?? '',
        timestamp: dayjs(v.timestamp).utc().local().format('YYYY/MM/DD HH:mm'),
        place: insertPlace(v),
        urinatedToilet: insertBoolean(v.urinatedToilet),
        toiletUrineVolume: insertMlValue(v.toiletUrineVolume),
        padUrineVolumeType: insertPadUrineVolumeType(v),
        padUrineVolume: insertMlValue(v.padUrineVolume),
        ruv: insertMlValue(v.ruv),
        accumulation: v.accumulation ?? '',
        posture: insertPosture(v),
        urineLeakage: insertBoolean(v.urineLeakage),
        incontinence: insertIncontinence(v),
        isLeakOutUrine: insertBoolean(v.isLeakOutUrine),
        isUrgency: insertBoolean(v.isUrgency),
        defecation: insertDefecation(v),
        hardnessType: insertHardnessType(v),
        isLeakOutFeces: insertBoolean(v.isLeakOutFeces),
        diaperName: v.diaperName ?? '',
        diaper: insertDiaper(v),
        padFirstName: v.padFirstName ?? '',
        padFirst: insertMlValue(v.padFirst),
        padSecondName: v.padSecondName ?? '',
        padSecond: insertMlValue(v.padSecond),
        drinkName: insertDrinkingName(v, drinks),
        drinkingVolume: insertMlValue(v.drinkingVolume),
        laxative: insertLaxativeName(v, laxatives),
        laxativeType: insertLaxativeType(v),
        laxativeAmount: insertLaxativeAmount(v),
        memo: v.memo ? v.memo.replace(/\r?\n/g, '') : '',
        staffName: v.staffName ?? '',
        workingTime: v.workingTime ? String(v.workingTime) : '',
      };
      exportData.push(csvData);
    });
  });

  const filename = buildCsvFilename({
    currentNursingHomeName,
    residentFullName,
    csvTitle,
    start,
    end,
  });

  const column = showMedicalContent
    ? isJaLanguage()
      ? DIALY_CSV_COL_JA
      : DIALY_CSV_COL_EN
    : isJaLanguage()
      ? CARE_HISTORY_CSV_COL_JA
      : CARE_HISTORY_CSV_COL_EN;

  exportData = exportData.map((v, i) => {
    return { 'S.No': i + 1, ...v };
  });
  const columnWithSNo = [{ label: 'S.No', value: 'S.No' }, ...column];
  await exportCSV(exportData, columnWithSNo, filename);
  return true;
};
